import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { COMPANY } from "@/data/site";

const DEFAULT_TITLE = "JayesPay | Retailer Fintech Platform by Velariti";
const DEFAULT_DESCRIPTION =
  `${COMPANY.name} is a product of ${COMPANY.legalName}, offering recharge, bill payments, PAN card services, banking and smart wallet tools.`;

const DEFAULT_KEYWORDS = [
  "JayesPay",
  "Velariti",
  "retailer fintech app",
  "recharge app for retailers",
  "bill payment services",
  "PAN card services",
  "NSDL Bank BC",
  "recurring deposit app",
  "digital service counter",
].join(", ");

const SITE_URL = (process.env.REACT_APP_SITE_URL || "").replace(/\/$/, "");
const BRAND_SUFFIX = " | JayesPay";

const pageSeo = {
  "/": {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
  "/services": {
    title: "Services Catalogue | JayesPay",
    description:
      "Explore JayesPay live and upcoming retailer services including recharge, utility bill payments, PAN, NSDL Bank BC, recurring deposits, AePS, travel and more.",
    keywords:
      "JayesPay services, recharge services, bill payment services, PAN card, NSDL Bank BC, recurring deposit, AePS, retailer services",
  },
  "/features": {
    title: "Retailer App Features | JayesPay",
    description:
      "Discover JayesPay app features for retailers: smart dashboard, wallet, ledger reports, package upgrades, refer and earn, T-PIN, app lock and support.",
    keywords:
      "JayesPay features, retailer app dashboard, smart wallet, ledger reports, T-PIN, app lock, refer and earn",
  },
  "/packages": {
    title: "Retailer Packages | JayesPay",
    description:
      "Compare JayesPay retailer packages and choose the right activation plan for recharge, bill payments, PAN, banking and upcoming digital services.",
    keywords:
      "JayesPay packages, retailer package, fintech activation plan, JayesPay package, digital services package",
  },
  "/about": {
    title: "About Velariti and JayesPay",
    description:
      "Learn about JayesPay, a product of Velariti Software Solutions Private Limited, built to help Indian retailers offer digital financial services with confidence.",
    keywords:
      "about JayesPay, Velariti Software Solutions, retailer fintech company, Bihar fintech company",
  },
  "/contact": {
    title: "Contact JayesPay",
    description:
      "Contact the JayesPay team for retailer onboarding, support, partnership questions and product enquiries.",
    keywords:
      "contact JayesPay, JayesPay support, Velariti contact, retailer onboarding support",
  },
  "/become-partner": {
    title: "Become a JayesPay Partner",
    description:
      "Apply to become a JayesPay retail partner and turn your shop into a digital service counter for recharge, bills, PAN, banking and more.",
    keywords:
      "become JayesPay partner, retailer partner, fintech retailer onboarding, digital service counter",
  },
  "/disclaimer": {
    title: "Disclaimer | JayesPay",
    description:
      "Read the JayesPay disclaimer for platform information, service availability and partner service terms.",
    keywords: "JayesPay disclaimer, Velariti disclaimer",
  },
  "/privacy-policy": {
    title: "Privacy Policy | JayesPay",
    description:
      "Read the JayesPay privacy policy to understand how Velariti collects, uses and protects customer and retailer information.",
    keywords: "JayesPay privacy policy, Velariti privacy policy, data privacy",
  },
  "/refund-policy": {
    title: "Refund Policy | JayesPay",
    description:
      "Read the JayesPay refund policy for wallet, package, service transaction and payment related refund information.",
    keywords: "JayesPay refund policy, payment refund, wallet refund",
  },
  "/terms-and-conditions": {
    title: "Terms and Conditions | JayesPay",
    description:
      "Read the JayesPay terms and conditions for retailer account usage, services, payments, compliance and platform responsibilities.",
    keywords: "JayesPay terms, Velariti terms and conditions, retailer terms",
  },
  "/merchant-onboarding-policy": {
    title: "Merchant Onboarding Policy | JayesPay",
    description:
      "Read the JayesPay merchant onboarding policy covering eligibility, KYM and due diligence, risk assessment, service authorization and ongoing monitoring of retailers.",
    keywords:
      "JayesPay merchant onboarding policy, retailer KYC, know your merchant, merchant verification, Velariti policy",
  },
  "/merchant-settlement-policy": {
    title: "Merchant Settlement & Payout Policy | JayesPay",
    description:
      "Read the JayesPay merchant settlement and payout policy covering wallet balances, commissions, payout requests, reconciliation, refunds, holds and settlement disputes.",
    keywords:
      "JayesPay settlement policy, merchant payout policy, retailer commission settlement, wallet payout, Velariti policy",
  },
  "/account-deactivation": {
    title: "Account Deactivation | JayesPay",
    description:
      "Learn how to request JayesPay account deactivation and understand what happens to your retailer account and service data.",
    keywords:
      "JayesPay account deactivation, deactivate retailer account, delete JayesPay account",
  },
};

const notFoundSeo = {
  title: "Page Not Found | JayesPay",
  description:
    "The page you are looking for could not be found on the JayesPay website.",
  robots: "noindex, follow",
};

const referralSeo = {
  title: "You're Invited to Start Earning with JayesPay",
  description:
    "A JayesPay partner is already earning on recharge, bills, PAN and banking services. Install the retailer app now and their referral code applies automatically.",
  robots: "noindex, nofollow",
};

function normalizePath(pathname) {
  if (!pathname || pathname === "/") {
    return "/";
  }

  return pathname.replace(/\/+$/, "");
}

function getOrigin() {
  if (SITE_URL) {
    return SITE_URL;
  }

  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }

  return "";
}

function getCanonical(pathname) {
  const origin = getOrigin();
  const normalizedPath = normalizePath(pathname);

  if (!origin) {
    return normalizedPath;
  }

  return `${origin}${normalizedPath === "/" ? "" : normalizedPath}`;
}

function setMeta(selector, attrs) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attrs).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function setLink(selector, attrs) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attrs).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function setJsonLd(id, data) {
  let element = document.getElementById(id);

  if (!element) {
    element = document.createElement("script");
    element.id = id;
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(data);
}

export default function Seo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const normalizedPath = normalizePath(pathname);
    const seo = normalizedPath.startsWith("/ref/")
      ? referralSeo
      : pageSeo[normalizedPath] || notFoundSeo;
    const canonical = getCanonical(normalizedPath);
    const title = seo.title?.includes("JayesPay")
      ? seo.title
      : `${seo.title}${BRAND_SUFFIX}`;
    const description = seo.description || DEFAULT_DESCRIPTION;
    const keywords = seo.keywords || DEFAULT_KEYWORDS;
    const robots = seo.robots || "index, follow";
    const image = COMPANY.logo;

    document.title = title;

    setMeta('meta[name="description"]', { name: "description", content: description });
    setMeta('meta[name="keywords"]', { name: "keywords", content: keywords });
    setMeta('meta[name="robots"]', { name: "robots", content: robots });
    setMeta('meta[name="author"]', { name: "author", content: COMPANY.legalName });

    setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    setMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "JayesPay" });
    setMeta('meta[property="og:title"]', { property: "og:title", content: title });
    setMeta('meta[property="og:description"]', { property: "og:description", content: description });
    setMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    setMeta('meta[property="og:image"]', { property: "og:image", content: image });

    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });

    setLink('link[rel="canonical"]', { rel: "canonical", href: canonical });

    setJsonLd("seo-organization-jsonld", {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: COMPANY.legalName,
      alternateName: COMPANY.name,
      url: getOrigin() || canonical,
      logo: COMPANY.logo,
      email: COMPANY.email,
      telephone: COMPANY.phone,
      address: COMPANY.address,
    });

    setJsonLd("seo-website-jsonld", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: COMPANY.name,
      url: getOrigin() || canonical,
      publisher: {
        "@type": "Organization",
        name: COMPANY.legalName,
      },
    });
  }, [pathname]);

  return null;
}
